import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { X, User, Search } from "lucide-react";

/** Auto-suggest picker for another employee (their `emp_id` acts as the FK).
 * - `value` is the selected reporting-manager emp_id (or empty).
 * - `onChange(empId, name)` fires on pick/clear.
 * - `excludeEmpId` (optional) prevents picking self.
 */
export default function ReportingManagerCombobox({ value, onChange, excludeEmpId }) {
  const [emps, setEmps] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/employees", { params: { limit: 500, page: 1 } })
      .then(r => setEmps(r.data.data || r.data || []))
      .catch(() => {});
  }, []);

  const selected = useMemo(() => emps.find(e => e.emp_id === value), [emps, value]);
  const suggestions = useMemo(() => {
    if (!q) return [];
    const s = q.toLowerCase();
    return emps
      .filter(e => e.emp_id !== excludeEmpId)
      .filter(e =>
        (e.emp_id || "").toLowerCase().includes(s) ||
        (e.name || "").toLowerCase().includes(s) ||
        (e.designation || "").toLowerCase().includes(s)
      )
      .slice(0, 8);
  }, [emps, q, excludeEmpId]);

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 h-10 px-3 rounded-md border border-slate-200 bg-slate-50" data-testid="rm-picker-selected">
        <div className="flex items-center gap-2 min-w-0">
          <User className="h-4 w-4 text-sky-500 flex-shrink-0"/>
          <span className="truncate font-medium text-sm">{selected.name}</span>
          <span className="text-xs text-slate-400 font-mono">{selected.emp_id}</span>
        </div>
        <button type="button" onClick={() => onChange("", "")} className="p-1 rounded hover:bg-slate-200" data-testid="rm-picker-clear">
          <X className="h-3.5 w-3.5 text-slate-400"/>
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
      <Input
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search by name, ID, or designation…"
        className="pl-9"
        data-testid="rm-picker-input"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid="rm-picker-suggestions">
          {suggestions.map(e => (
            <button
              key={e.id}
              type="button"
              onMouseDown={() => { onChange(e.emp_id, e.name); setQ(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-sky-50 border-b border-slate-100 last:border-0"
              data-testid={`rm-picker-option-${e.emp_id}`}
            >
              <div className="font-medium text-sm">{e.name}</div>
              <div className="text-xs text-slate-500">
                <span className="font-mono text-sky-700">{e.emp_id}</span>
                {e.designation && <span> · {e.designation}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && q && suggestions.length === 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg px-3 py-2 text-xs text-slate-400">
          No matches for &ldquo;{q}&rdquo;
        </div>
      )}
    </div>
  );
}
